import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Clapperboard, Plus, ChevronRight, RefreshCw, ListChecks } from 'lucide-react'
import { api } from '../api'
import type { Job, Role } from '../types'
import { atLeast } from '../types'
import { go } from '../router'
import { Button, EmptyState, ErrorText, Menu, Skeleton, StatusBadge } from '../components/ui'
import { AuthImage, DownloadFileButton } from '../components'
import { usd, ago, modelName } from '../lib/utils'

const RUNNING = new Set(['queued', 'generating'])
// A failed or cancelled run can be picked back up. Resuming keeps the scenes
// that were already accepted, so a retry re-pays only for what is missing.
const RETRYABLE = new Set(['failed', 'aborted'])
type JobPage = { items: Job[]; next_cursor?: string; total: number; spent_cents: number }

function periodLabel(value: string) {
  const date = new Date(value)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const days = Math.round((today.getTime() - day.getTime()) / 86_400_000)
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days <= 7) return 'Previous 7 days'
  if (date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth()) return 'Earlier this month'
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
}

function groupJobs(rows: Job[]) {
  const groups = new Map<string, Job[]>()
  for (const row of rows) {
    const label = periodLabel(row.created_at)
    groups.set(label, [...(groups.get(label) ?? []), row])
  }
  return [...groups.entries()]
}

export function Jobs({ ws, role }: { ws: string; role?: Role }) {
  const jobs = useInfiniteQuery({
    queryKey: ['jobs', ws, 'pages'],
    initialPageParam: '',
    queryFn: ({ pageParam }) => api.get<JobPage>(
      `/api/workspaces/${ws}/jobs-page?limit=20${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`),
    getNextPageParam: last => last.next_cursor || undefined,
    // Poll while anything is running, so the list reflects a worker's progress
    // without anyone reloading.
    refetchInterval: q => {
      const data = q.state.data as { pages?: JobPage[] } | undefined
      return data?.pages?.some(page => page.items.some(j => RUNNING.has(j.status))) ? 4000 : false
    },
  })

  const qc = useQueryClient()
  const retry = useMutation({
    mutationFn: (id: string) => api.post(`/api/workspaces/${ws}/jobs/${id}/queue`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs', ws] }),
  })

  const rows = jobs.data?.pages.flatMap(page => page.items) ?? []
  const total = jobs.data?.pages[0]?.total ?? 0
  const spent = jobs.data?.pages[0]?.spent_cents ?? 0
  const groups = groupJobs(rows)
  const canMake = atLeast(role, 'member')
  const canEdit = canMake

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl">Videos</h1>
          <p className="mt-1 text-sm text-ink-faint">
            {total > 0
              ? `${total} video${total === 1 ? '' : 's'} · ${usd(spent)} spent`
              : 'Everything you have made in this workspace.'}
          </p>
        </div>
        {canMake && (
          <Button variant="primary" onClick={() => go({ name: 'new-job' })}>
            <Plus /> New video
          </Button>
        )}
      </div>

      <ErrorText error={jobs.error} />

      {jobs.isLoading && (
        <div className="space-y-2">
          {[0, 1, 2].map(i => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      )}

      {!jobs.isLoading && rows.length === 0 && (
        <EmptyState
          icon={Clapperboard}
          title="No videos yet"
          body="You will need a brand, an avatar and a look before you can make one. Setup lives in the sidebar."
          action={canMake
            ? <Button variant="primary" onClick={() => go({ name: 'getting-started' })}>
                <ListChecks /> Getting started
              </Button>
            : undefined}
        />
      )}

      {rows.length > 0 && (
        <div className="space-y-7">
          {groups.map(([period, periodJobs]) => (
            <section key={period} className="space-y-2">
              <h2 className="text-xs font-medium uppercase tracking-[0.08em] text-ink-faint">{period}</h2>
              {periodJobs.map(j => (
            <div
              key={j.id}
              className="panel group flex w-full items-center gap-4 p-4 text-left transition-colors hover:border-line-strong hover:bg-surface-2/40"
            >
              {/* A still from the finished ad. Without it the list is a column
                  of identical badges and the only way to tell two ads apart is
                  to open both. */}
              {j.poster_key ? (
                <button
                  onClick={() => go({ name: 'job', id: j.id })}
                  className="w-12 shrink-0 overflow-hidden rounded-[--radius-control]"
                >
                  <AuthImage
                    ws={ws} storageKey={j.poster_key} alt="" ratio="portrait"
                    className="w-full border-0"
                  />
                </button>
              ) : (
                <StatusBadge status={j.status} />
              )}

              <button
                onClick={() => go({ name: 'job', id: j.id })}
                className="min-w-0 flex-1 text-left"
              >
                <div className="flex items-center gap-2">
                  <div className="truncate font-medium">
                    {j.pain_point || <span className="text-ink-faint">Untitled video</span>}
                  </div>
                  {j.poster_key && <StatusBadge status={j.status} />}
                </div>
                <div className="mt-0.5 truncate text-xs text-ink-faint">
                  {j.error
                    ? <span className="text-bad">{j.error}</span>
                    : (
                      <>
                        {j.stage || `Created ${ago(j.created_at)}${j.creator_name ? ` by ${j.creator_name}` : ''}`}
                        {j.video_model && <> · {modelName(j.video_model)}</>}
                      </>
                    )}
                </div>
              </button>

              <div className="shrink-0 text-right">
                <div className="text-sm tabular-nums">{usd(j.cost_cents)}</div>
                <div className="text-[11px] text-ink-faint">{ago(j.created_at)}</div>
              </div>

              {/* Retry where the failure is, rather than making someone open
                  the ad to find the button. Resuming keeps every scene that
                  was already accepted, so this re-pays only for what is
                  missing. */}
              {canEdit && RETRYABLE.has(j.status) && (
                <Button
                  variant="secondary" size="sm" className="shrink-0"
                  disabled={retry.isPending && retry.variables === j.id}
                  onClick={() => retry.mutate(j.id)}
                >
                  <RefreshCw /> {retry.isPending && retry.variables === j.id ? 'Starting…' : 'Retry'}
                </Button>
              )}

              {j.output_key && (
                <DownloadFileButton
                  ws={ws}
                  storageKey={j.output_key}
                  filename={`reelworks-video-${j.id}.mp4`}
                  className="shrink-0"
                />
              )}

			  {canMake && j.output_key && (
                <Menu
                  className="shrink-0"
                  label={`Actions for ${j.pain_point || 'video'}`}
				  actions={[{
					  label: 'Regenerate',
					  icon: RefreshCw,
					  onSelect: () => go({ name: 'new-job' as const, sourceID: j.id, regenerationMode: 'scenes' as const }),
				  }]}
                />
              )}

              <button onClick={() => go({ name: 'job', id: j.id })} className="shrink-0">
                <ChevronRight className="size-4 text-ink-faint transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>
              ))}
            </section>
          ))}
          {jobs.hasNextPage && (
            <div className="flex justify-center pt-1">
              <Button variant="secondary" onClick={() => jobs.fetchNextPage()} disabled={jobs.isFetchingNextPage}>
                {jobs.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
